import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SwitchRow } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { localizedName } from '@/lib/localized';
import type { CatalogItem } from '@/Pages/Catalog/lib';
import type { MenuChannel } from '@/lib/shared-types';
import { menuVersionRulePath, menuVersionRulesPath } from '../menuGovernanceRoutes';
import type { BranchMenuRuleRow, MenuVersionDetail } from '../menuGovernanceApi';

const CHANNELS: MenuChannel[] = ['DINE_IN', 'TAKEAWAY', 'DELIVERY', 'ONLINE'];
const CHANNEL_KEY: Record<MenuChannel, string> = {
  DINE_IN: 'menuGovernance.editor.channelDineIn',
  TAKEAWAY: 'menuGovernance.editor.channelTakeaway',
  DELIVERY: 'menuGovernance.editor.channelDelivery',
  ONLINE: 'menuGovernance.editor.channelOnline',
};

/**
 * Screen 2 — branch assortment, per-branch price overrides and channel
 * availability. One rule is one `(branch, item)` pair, and each row here
 * writes exactly one — `PUT …/rules` is idempotent on that pair
 * (`UpsertMenuRulesDto`'s own doc), so re-saving a row converges rather than
 * duplicating it.
 *
 * Only offered on a `DRAFT` or `IN_REVIEW` version the actor may draft —
 * `MenuVersionService.loadEditable` refuses a write on anything else with
 * `MENU_VERSION_NOT_EDITABLE`, and the read-only table below is what everyone
 * else, and every other status, sees instead.
 */
export default function RuleEditor({
  version,
  editable,
  onChanged,
}: {
  version: MenuVersionDetail;
  editable: boolean;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm().confirm;

  const [form, setForm] = useState<{ open: boolean; rule: BranchMenuRuleRow | null }>({
    open: false,
    rule: null,
  });
  const [removing, setRemoving] = useState<string | null>(null);

  // `GET /catalog` is `menu.view`, a different key from this screen's own
  // `menu_governance.view` — every governance-holding role is an admin/owner
  // and holds it too in practice, but the read is still gated honestly rather
  // than assumed, the same way `ApprovalDetail`'s `AffectedItems` gates its
  // own second fetch on `orders.view` rather than the key that opened it.
  const canReadCatalog = can('menu.view');
  const itemsQuery = useApi<unknown>(canReadCatalog ? '/catalog' : null, { limit: 200 });
  const items = useMemo(() => rowsOf<CatalogItem>(itemsQuery.data).rows, [itemsQuery.data]);
  const itemName = (itemId: string, fallback: string) => {
    const item = items.find((i) => i.id === itemId);
    return item ? localizedName(item, i18n.language) : fallback;
  };

  const remove = async (rule: BranchMenuRuleRow) => {
    const ok = await confirm({
      title: t('menuGovernance.editor.removeConfirmTitle'),
      body: t('menuGovernance.editor.removeConfirmBody', {
        item: itemName(rule.itemId, rule.item.name),
        branch: rule.branch.name,
      }),
      confirmLabel: t('menuGovernance.editor.removeRule'),
      tone: 'danger',
    });
    if (!ok) return;
    setRemoving(rule.id);
    try {
      await api.delete(menuVersionRulePath(version.id, rule.id));
      toast.success(t('menuGovernance.toast.ruleRemoved'));
      onChanged();
    } catch {
      toast.error(t('menuGovernance.refusal.genericBody'));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('menuGovernance.detail.sectionRules')}</h3>
        {editable && version.targets.length > 0 && (
          <Button size="sm" onClick={() => setForm({ open: true, rule: null })}>
            <Plus className="me-1.5 h-4 w-4" />
            {t('menuGovernance.editor.addRule')}
          </Button>
        )}
      </div>

      {!editable && (
        <p className="text-sm text-ink-muted">
          {t('menuGovernance.editor.notEditableHint', {
            status: t(`menuGovernance.status.${version.status}`),
          })}
        </p>
      )}

      {version.rules.length === 0 ? (
        <EmptyState
          title={t('menuGovernance.editor.noRulesTitle')}
          hint={t('menuGovernance.editor.noRulesHint')}
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('menuGovernance.editor.branch')}</TH>
                <TH>{t('menuGovernance.editor.item')}</TH>
                <TH>{t('menuGovernance.editor.carried')}</TH>
                <TH>{t('menuGovernance.editor.price')}</TH>
                <TH>{t('menuGovernance.editor.channels')}</TH>
                <TH className="text-end">{t('menuGovernance.editor.sortOrder')}</TH>
                {editable && <TH />}
              </TR>
            </THead>
            <tbody>
              {version.rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  editable={editable}
                  itemName={itemName(rule.itemId, rule.item.name)}
                  busy={removing === rule.id}
                  onEdit={() => setForm({ open: true, rule })}
                  onRemove={() => remove(rule)}
                />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {form.open && (
        <RuleFormDialog
          version={version}
          rule={form.rule}
          items={items}
          canReadCatalog={canReadCatalog}
          onClose={() => setForm({ open: false, rule: null })}
          onSaved={() => {
            setForm({ open: false, rule: null });
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function RuleRow({
  rule,
  editable,
  itemName,
  busy,
  onEdit,
  onRemove,
}: {
  rule: BranchMenuRuleRow;
  editable: boolean;
  itemName: string;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency(rule.branchId);

  return (
    <TR>
      <TD>{rule.branch.name}</TD>
      <TD className="font-medium">{itemName}</TD>
      <TD>
        <Badge tone={rule.carried ? 'success' : 'danger'}>
          {t(rule.carried ? 'menuGovernance.common.carried' : 'menuGovernance.common.notCarried')}
        </Badge>
      </TD>
      <TD className="tabular-nums">
        {rule.price === null ? (
          <span className="text-ink-muted">
            {t('menuGovernance.editor.inherits', { price: format(rule.item.basePrice) })}
          </span>
        ) : (
          format(rule.price)
        )}
      </TD>
      <TD>
        {rule.channels.length === 0
          ? t('menuGovernance.history.allChannels')
          : rule.channels.map((c) => t(CHANNEL_KEY[c])).join(', ')}
      </TD>
      <TD className="text-end tabular-nums">{rule.sortOrder ?? '—'}</TD>
      {editable && (
        <TD className="text-end whitespace-nowrap">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            {t('menuGovernance.editor.editRule')}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove}>
            {t('menuGovernance.editor.removeRule')}
          </Button>
        </TD>
      )}
    </TR>
  );
}

function RuleFormDialog({
  version,
  rule,
  items,
  canReadCatalog,
  onClose,
  onSaved,
}: {
  version: MenuVersionDetail;
  rule: BranchMenuRuleRow | null;
  items: CatalogItem[];
  canReadCatalog: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();

  const [branchId, setBranchId] = useState(rule?.branchId ?? version.targets[0]?.branchId ?? '');
  const [itemId, setItemId] = useState(rule?.itemId ?? '');
  const [itemSearch, setItemSearch] = useState('');
  const [carried, setCarried] = useState(rule?.carried ?? true);
  const [priceText, setPriceText] = useState(rule?.price ?? '');
  const [channels, setChannels] = useState<MenuChannel[]>(rule?.channels ?? []);
  const [sortOrder, setSortOrder] = useState(rule?.sortOrder != null ? String(rule.sortOrder) : '');
  const [nameEn, setNameEn] = useState(rule?.translations.find((tr) => tr.locale === 'en')?.name ?? '');
  const [nameAr, setNameAr] = useState(rule?.translations.find((tr) => tr.locale === 'ar')?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { format, toMoney } = useCurrency(branchId || null);
  const selectedItem = items.find((i) => i.id === itemId) ?? null;

  const shownItems = useMemo(() => {
    const needle = itemSearch.trim().toLowerCase();
    if (!needle) return items.slice(0, 50);
    return items
      .filter((i) => localizedName(i, i18n.language).toLowerCase().includes(needle))
      .slice(0, 50);
  }, [items, itemSearch, i18n.language]);

  const toggleChannel = (c: MenuChannel) =>
    setChannels((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const branchError = submitted && !branchId ? t('menuGovernance.editor.branchRequired') : undefined;
  const itemError = submitted && !itemId ? t('menuGovernance.editor.itemRequired') : undefined;
  const priceInvalid = priceText.trim() !== '' && Number.isNaN(Number(priceText));
  const priceError = submitted && priceInvalid ? t('menuGovernance.editor.priceInvalid') : undefined;
  const translationMismatch = Boolean(nameEn.trim()) !== Boolean(nameAr.trim());
  const translationError =
    submitted && translationMismatch ? t('menuGovernance.editor.partialTranslationError') : undefined;

  const save = async () => {
    setSubmitted(true);
    if (!branchId || !itemId || priceInvalid || translationMismatch) return;
    setSaving(true);
    try {
      await api.put(menuVersionRulesPath(version.id), {
        rules: [
          {
            branchId,
            itemId,
            carried,
            price: priceText.trim() === '' ? null : toMoney(priceText.trim()),
            channels,
            sortOrder: sortOrder.trim() === '' ? undefined : Number(sortOrder),
            ...(nameEn.trim() && nameAr.trim()
              ? { translations: [{ locale: 'en', name: nameEn.trim() }, { locale: 'ar', name: nameAr.trim() }] }
              : {}),
          },
        ],
      });
      toast.success(t('menuGovernance.toast.rulesSaved'));
      onSaved();
    } catch {
      toast.error(t('menuGovernance.refusal.genericBody'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(rule ? 'menuGovernance.editor.editRule' : 'menuGovernance.editor.addRule')}
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('menuGovernance.editor.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {t('menuGovernance.editor.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('menuGovernance.editor.branch')} error={branchError} required>
          <Select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            disabled={Boolean(rule)}
          >
            <option value="" disabled>
              {t('menuGovernance.editor.branch')}
            </option>
            {version.targets.map((tgt) => (
              <option key={tgt.branchId} value={tgt.branchId}>
                {tgt.branch.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('menuGovernance.editor.item')} error={itemError} required>
          {rule ? (
            <Input value={selectedItem ? localizedName(selectedItem, i18n.language) : rule.item.name} disabled />
          ) : !canReadCatalog ? (
            <p className="text-sm text-ink-muted">—</p>
          ) : (
            <div className="space-y-1.5">
              <Input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder={t('menuGovernance.editor.item')}
              />
              <div className="max-h-40 overflow-y-auto rounded-lg border">
                {shownItems.map((i) => (
                  <button
                    type="button"
                    key={i.id}
                    onClick={() => setItemId(i.id)}
                    className={
                      'flex w-full items-center justify-between px-3 py-1.5 text-start text-sm hover:bg-hover ' +
                      (itemId === i.id ? 'bg-hover font-medium' : '')
                    }
                  >
                    <span>{localizedName(i, i18n.language)}</span>
                    <span className="tabular-nums text-ink-muted">{format(i.basePrice)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Field>

        <SwitchRow
          label={t('menuGovernance.editor.carried')}
          hint={t('menuGovernance.editor.carriedHint')}
          checked={carried}
          onChange={setCarried}
        />

        <Field
          label={t('menuGovernance.editor.price')}
          error={priceError}
          hint={
            selectedItem
              ? t('menuGovernance.editor.priceHint', { price: format(selectedItem.basePrice) })
              : undefined
          }
        >
          <Input
            value={priceText}
            onChange={(e) => setPriceText(e.target.value)}
            placeholder={t('menuGovernance.editor.pricePlaceholder')}
            inputMode="decimal"
          />
        </Field>

        <Field label={t('menuGovernance.editor.channels')} hint={t('menuGovernance.editor.channelsHint')}>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {CHANNELS.map((c) => (
              <label key={c} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={channels.includes(c)}
                  onChange={() => toggleChannel(c)}
                  className="h-4 w-4 accent-[var(--accent-solid)]"
                />
                {t(CHANNEL_KEY[c])}
              </label>
            ))}
          </div>
        </Field>

        <Field label={t('menuGovernance.editor.sortOrder')} hint={t('menuGovernance.editor.sortOrderHint')}>
          <Input
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            inputMode="numeric"
          />
        </Field>

        <Field
          label={t('menuGovernance.editor.translations')}
          hint={t('menuGovernance.editor.translationsHint')}
          error={translationError}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder={t('menuGovernance.editor.nameEn')}
            />
            <Input
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              placeholder={t('menuGovernance.editor.nameAr')}
              dir="rtl"
            />
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
